import React, { useCallback, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Popover,
  Stack,
  Switch,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import LayersIcon from '@mui/icons-material/Layers'
import ZoomInIcon from '@mui/icons-material/ZoomIn'
import { msg, Trans } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { maplibreGroupChildButtonSx } from '../../consts/map-floating-control-sx'
import {
  COMMUNITY_MAP_LAYER_ATTRIBUTE_WARNING_CODES,
  resolveCommunityMapLayerAttributeWarning,
} from '../../utils/community-map-layer-utils'
import SvgCustomIcon from '../icons/SvgCustomIcon'

const PALETTE_GRADIENTS = {
  red: ['#DDB100', '#DD8500', '#DD5800', '#DD2C00', '#DD0000'],
  blue: ['#00A0CB', '#0079B8', '#0051A6', '#002A93', '#000280'],
  gray: ['#BBBEBF', '#959798', '#6F7171', '#494A4A', '#232323']
}

function getPaletteGradient(layer) {
  const colors = PALETTE_GRADIENTS[layer.palette]
  if (colors) return colors.join(',')
  const c = layer.color || '#6366F1'
  return `${c},${c}`
}

function buildAttributeWarningMessage(warning, translate) {
  if (!warning) return null

  const attributeName = warning.mapAttribute
  if (warning.code === COMMUNITY_MAP_LAYER_ATTRIBUTE_WARNING_CODES.MISSING_PROPERTY) {
    return translate(
      msg`Attribute "${attributeName}" is missing from all features. Color gradation is disabled.`,
    )
  }
  if (warning.code === COMMUNITY_MAP_LAYER_ATTRIBUTE_WARNING_CODES.NOT_NUMERIC) {
    return translate(
      msg`Attribute "${attributeName}" is not numeric on any feature. Color gradation is disabled.`,
    )
  }
  if (warning.code === COMMUNITY_MAP_LAYER_ATTRIBUTE_WARNING_CODES.PARTIAL_MISSING) {
    const missingCount = warning.totalCount - warning.validCount
    return translate(
      msg`Attribute "${attributeName}" is missing or invalid on ${missingCount} of ${warning.totalCount} features.`,
    )
  }
  return null
}

function buildLayerTogglePatch(item, layerId, checked) {
  if (Number(item.id) !== Number(layerId)) {
    return { ...item, isFocused: false }
  }
  if (checked) {
    return {
      ...item,
      isOpen: true,
      isFocused: true,
      focusNonce: Date.now(),
    }
  }
  return {
    ...item,
    isOpen: false,
    isFocused: false,
    isLoading: false,
    fileContent: null,
    isLoaded: false,
  }
}

const LayerMapButton = React.memo(
  ({
    layersList,
    setLayersList,
    isFormOpen,
    isPositioning,
    isPositionSet,
    canReorderMapLayers = false,
    onFocusMapLayer,
    onMoveLayerUp,
    onMoveLayerDown,
    isMapLayerReordering = false
  }) => {
    const [anchorEl, setAnchorEl] = useState(null)
    const theme = useTheme()
    const { _ } = useLingui()

    const isLight = theme.palette.mode === 'light'
    const textPrimary = isLight
      ? theme.palette.newColors?.gray || theme.palette.text.primary
      : theme.palette.newColors?.lightGray || theme.palette.text.primary
    const textSecondary = theme.palette.text.secondary
    const surfaceBg = isLight ? theme.palette.background.paper : theme.palette.background.default
    const rowBorder = theme.palette.divider
    const rowHoverBg = isLight ? 'rgba(99, 102, 241, 0.04)' : 'rgba(255, 255, 255, 0.04)'
    const activeRowBg = isLight ? 'rgba(99, 102, 241, 0.08)' : 'rgba(99, 102, 241, 0.16)'

    const handleButtonClick = (event) => {
      setAnchorEl(anchorEl ? null : event.currentTarget)
    }

    const requestLayerFocus = useCallback((layerId) => {
      const targetLayerId = Number(layerId)
      setLayersList((prev) => prev.map((item) => {
        if (Number(item.id) !== targetLayerId) {
          return { ...item, isFocused: false }
        }
        return {
          ...item,
          isOpen: true,
          isFocused: true,
          focusNonce: Date.now(),
        }
      }))
      window.setTimeout(() => {
        onFocusMapLayer?.(targetLayerId)
      }, 0)
    }, [setLayersList, onFocusMapLayer])

    const open = Boolean(anchorEl)
    const id = open ? 'map-layer-popover' : undefined
    const reorderDisabled =
      isFormOpen || isPositioning || isPositionSet || isMapLayerReordering

    const actionBtnSx = {
      width: 24,
      height: 24,
      border: `1px solid ${rowBorder}`,
      borderRadius: 'var(--radius-md, 6px)',
      color: textSecondary,
      '&:hover:not(.Mui-disabled)': {
        backgroundColor: rowHoverBg,
        color: theme.palette.primary.main
      },
      '&.Mui-disabled': {
        opacity: 0.35
      }
    }

    return (
      <>
        <Button
          type="button"
          className="fred-map-toolbar-btn"
          aria-describedby={id}
          onClick={handleButtonClick}
          sx={maplibreGroupChildButtonSx(theme)}
        >
          <SvgCustomIcon color="#425B61" name="stacks" size={24} />
        </Button>

        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          disableAutoFocus
          disableEnforceFocus
          disableRestoreFocus
          slotProps={{
            paper: {
              sx: {
                borderRadius: 'var(--radius-xl, 10px)',
                boxShadow: theme.shadows[8],
                overflow: 'hidden',
                minWidth: 280,
                maxWidth: 340
              }
            }
          }}
        >
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              bgcolor: surfaceBg,
              display: 'flex',
              flexDirection: 'column',
              maxHeight: 'min(360px, 65vh)'
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.75}
              sx={{
                px: 1.5,
                py: 1,
                borderBottom: `1px solid ${rowBorder}`,
                bgcolor: isLight ? 'var(--color-background, #F8FAFC)' : 'transparent'
              }}
            >
              <LayersIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
              <Typography
                sx={{
                  flex: 1,
                  fontSize: 'var(--font-md, 13px)',
                  fontWeight: 700,
                  color: textPrimary
                }}
              >
                <Trans>Additional Data</Trans>
              </Typography>
              {isMapLayerReordering && (
                <CircularProgress size={16} thickness={5} />
              )}
            </Stack>

            <Box sx={{ overflowY: 'auto', px: 1, py: 0.75, flex: 1 }}>
              {layersList.length === 0 ? (
                <Typography
                  variant="body2"
                  sx={{ px: 0.5, py: 1.5, textAlign: 'center', color: textSecondary, fontSize: 'var(--font-sm, 11px)' }}
                >
                  <Trans>No map layers available</Trans>
                </Typography>
              ) : (
                <Stack spacing={0.5}>
                  {layersList.map((layer, i) => {
                    const isFirst = i === 0
                    const isLast = i === layersList.length - 1
                    const formatLabel = layer.fileExtension || 'geojson'
                    const title = layer.title || layer.description || _(msg`Untitled layer`)
                    const attributeWarning = resolveCommunityMapLayerAttributeWarning(layer)
                    const attributeWarningMessage = buildAttributeWarningMessage(attributeWarning, _)

                    return (
                      <Box
                        key={layer.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          px: 0.75,
                          py: 0.625,
                          borderRadius: 'var(--radius-md, 6px)',
                          border: `1px solid ${layer.isOpen ? theme.palette.primary.main : rowBorder}`,
                          bgcolor: layer.isOpen ? activeRowBg : 'transparent',
                          transition: 'background-color 0.15s ease, border-color 0.15s ease',
                          '&:hover': {
                            bgcolor: layer.isOpen ? activeRowBg : rowHoverBg
                          }
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.375 }}>
                            <Typography
                              variant="body2"
                              title={title}
                              sx={{
                                flex: 1,
                                fontWeight: 600,
                                fontSize: 'var(--font-sm, 11px)',
                                color: textPrimary,
                                lineHeight: 1.25,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}
                            >
                              {title}
                            </Typography>
                            <Chip
                              label={formatLabel}
                              size="small"
                              variant="outlined"
                              sx={{
                                height: 18,
                                fontSize: 'var(--font-xs, 10px)',
                                fontWeight: 600,
                                flexShrink: 0,
                                borderColor: rowBorder,
                                color: textSecondary,
                                '& .MuiChip-label': { px: 0.75 }
                              }}
                            />
                          </Stack>
                          <Box
                            aria-hidden
                            sx={{
                              height: 2,
                              width: '100%',
                              borderRadius: 'var(--radius-full, 9999px)',
                              backgroundImage: `linear-gradient(to right, ${getPaletteGradient(layer)})`
                            }}
                          />
                          {layer.isLoading && (
                            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.375 }}>
                              <CircularProgress size={10} thickness={5} />
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 'var(--font-xs, 10px)' }}>
                                <Trans>Loading…</Trans>
                              </Typography>
                            </Stack>
                          )}
                          {attributeWarningMessage && (
                            <Alert
                              severity="warning"
                              sx={{
                                mt: 0.5,
                                py: 0,
                                px: 0.75,
                                fontSize: 'var(--font-xs, 10px)',
                                alignItems: 'flex-start',
                                '& .MuiAlert-message': { py: 0.375, lineHeight: 1.35 },
                                '& .MuiAlert-icon': { fontSize: 14, py: 0.5, mr: 0.5 },
                              }}
                            >
                              {attributeWarningMessage}
                            </Alert>
                          )}
                        </Box>

                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={0.25}
                          sx={{ flexShrink: 0 }}
                        >
                          <Tooltip title={_(msg`Zoom to layer`)} placement="left">
                            <span>
                              <IconButton
                                size="small"
                                aria-label={_(msg`Zoom to layer`)}
                                disabled={layer.isLoading}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  requestLayerFocus(layer.id)
                                }}
                                sx={actionBtnSx}
                              >
                                <ZoomInIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                          {canReorderMapLayers && (
                            <Stack direction="column" spacing={0.125}>
                              <Tooltip title={_(msg`Move layer up`)} placement="left">
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={reorderDisabled || isFirst}
                                    aria-label={_(msg`Move layer up`)}
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      onMoveLayerUp?.(layer.id)
                                    }}
                                    sx={actionBtnSx}
                                  >
                                    <KeyboardArrowUpIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title={_(msg`Move layer down`)} placement="left">
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={reorderDisabled || isLast}
                                    aria-label={_(msg`Move layer down`)}
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      onMoveLayerDown?.(layer.id)
                                    }}
                                    sx={actionBtnSx}
                                  >
                                    <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Stack>
                          )}
                          <Switch
                            size="small"
                            checked={Boolean(layer.isOpen)}
                            disabled={layer.isLoading}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => {
                              const checked = e.target.checked
                              setLayersList((prev) => prev.map((item) => (
                                buildLayerTogglePatch(item, layer.id, checked)
                              )))
                            }}
                            sx={{
                              ml: 0,
                              transform: 'scale(0.85)',
                              transformOrigin: 'center',
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: theme.palette.primary.main
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: theme.palette.primary.main
                              }
                            }}
                          />
                        </Stack>
                      </Box>
                    )
                  })}
                </Stack>
              )}
            </Box>

            {canReorderMapLayers && layersList.length > 1 && (
              <Typography
                variant="caption"
                sx={{
                  px: 1.5,
                  py: 0.75,
                  borderTop: `1px solid ${rowBorder}`,
                  color: textSecondary,
                  fontSize: 'var(--font-xs, 10px)',
                  bgcolor: isLight ? 'var(--color-background, #F8FAFC)' : 'transparent'
                }}
              >
                <Trans>Top of the list is drawn above on the map.</Trans>
              </Typography>
            )}
          </Box>
        </Popover>
      </>
    )
  }
)
export default LayerMapButton
